/**
 * "De zaak" en "De review" bij een review (ontwerp v4 §7).
 *
 * Drie acties uit de mock, elk met de stand die ze werkelijk hebben:
 *
 * - **Kortingscode privé.** Bestaat als motor, maar hangt nog aan een
 *   retour; de actie-laag meldt hem als `nog_niet_aangesloten`. De knop
 *   staat dus uit mét die reden. Niet stil verbergen.
 * - **Melding bij de vervoerder.** Dat is `case_tap`, en die is
 *   werkend. Hij heeft een trackingcode nodig; die komt uit het
 *   zaak-detail van bouwer 1. Is er geen code, dan staat de knop uit
 *   met precies die reden erbij.
 * - **Klant privé mailen.** Geen eigen mailvenster: dit zet de gedeelde
 *   reactietegel in beeld, die van bouwer 2, met alles wat daar al in
 *   zit (adressen, handtekening, wachtrij, ongedaan maken).
 *
 * De rij "Regels" blijft leeg met uitleg. Bouwer 1 vond er in ronde 1
 * geen deterministische bron voor en dat is hier niet anders; een regel
 * die er soms naast zit is slechter dan een eerlijke lege regel.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  kaartenApi,
  werkplekApi,
  type ReviewDossier,
  type WerkplekActie,
} from "@/lib/werkplek-api";

import {
  IcoonBeheer,
  IcoonEuro,
  IcoonExtern,
  IcoonMail,
  IcoonPijlOnder,
  IcoonSchild,
} from "../Vandaag/pictogrammen";
import { Zwevendmenu } from "../Vandaag/Zwevendmenu";
import { datumTijd } from "../Vandaag/tijd";
import "../Vandaag/vandaag.css";
import "../Retouren/retouren.css";

interface Props {
  dossier: ReviewDossier;
  zaakId: string | null;
  /** Wissel de tegel naar de gedeelde reactietegel (privé mailen). */
  opPriveMail: () => void;
}

export function ReviewBlok({ dossier, zaakId, opPriveMail }: Props) {
  const [open, setOpen] = useState(false);
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseTekst, setCaseTekst] = useState("");
  const [uitkomst, setUitkomst] = useState<WerkplekActie | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const omhulsel = useRef<HTMLSpanElement | null>(null);
  const [knop, setKnop] = useState<HTMLButtonElement | null>(null);

  const soorten = useQuery({
    queryKey: ["werkplek", "actiesoorten"],
    queryFn: werkplekApi.actiesoorten,
    staleTime: 5 * 60_000,
  });

  const zaak = useQuery({
    queryKey: ["werkplek", "zaak", zaakId],
    queryFn: () => kaartenApi.detail(zaakId as string),
    enabled: Boolean(zaakId),
  });

  useEffect(() => {
    if (!open) return;
    function buiten(gebeurtenis: MouseEvent) {
      const doel = gebeurtenis.target as Node;
      // Het menu hangt in een portal aan `document.body`.
      if (omhulsel.current?.contains(doel)) return;
      if ((doel as HTMLElement)?.closest?.(".wp-actiemenu-zwevend")) return;
      setOpen(false);
    }
    function ontsnap(gebeurtenis: KeyboardEvent) {
      if (gebeurtenis.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", ontsnap);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", ontsnap);
    };
  }, [open]);

  const zending = zaak.data?.zending ?? null;
  const trackingcode = zending?.code ?? null;

  const kortingscode = (soorten.data ?? []).find(
    (rij) => rij.soort === "kortingscode",
  );
  const caseSoort = (soorten.data ?? []).find((rij) => rij.soort === "case_tap");
  const caseKan = caseSoort?.stand === "werkend" && Boolean(trackingcode);
  const caseRedenUit = !trackingcode
    ? "Er hangt geen trackingcode aan deze zaak; zonder code neemt de vervoerder geen melding aan."
    : (caseSoort?.reden_uit ?? null);

  const meldAan = useMutation({
    mutationFn: async () => {
      const actie = await werkplekApi.stelActieVoor({
        soort: "case_tap",
        zaak_id: zaakId,
        klant_id: dossier.klant_id,
        bevestigtekst: `Melding bij ${zending?.vervoerder ?? "de vervoerder"}`,
        parameters: {
          tracking_code: trackingcode,
          onderwerp: `Bezorgklacht uit een ${dossier.bron_label}-review`,
          bericht: caseTekst,
        },
      });
      return werkplekApi.bevestigActie(actie.id);
    },
    onSuccess: (actie) => {
      setCaseOpen(false);
      setUitkomst(actie);
      setFout(null);
    },
    onError: () =>
      setFout(
        "De melding is niet aangenomen door de vervoerder. Er staat niets open.",
      ),
  });

  return (
    <>
      <div className="wp-blok">
        <h4>
          <span>De review</span>
          <span className="wp-r">
            <span className="wp-chip wp-chip-grijs">publiek</span>
            <span
              className={`wp-chip ${dossier.negatief ? "wp-chip-risico" : "wp-chip-groen"}`}
            >
              {dossier.score_label}
            </span>
          </span>
        </h4>
        <div className="wp-review-tekst">
          {dossier.tekst ? (
            `"${dossier.tekst}"`
          ) : (
            <span className="wp-leegregel">
              Deze review heeft geen tekst, alleen een cijfer.
            </span>
          )}
          <span className="wp-meta">
            {dossier.naam}
            {dossier.geplaatst_op
              ? ` · geplaatst ${datumTijd(dossier.geplaatst_op)}`
              : ""}
            {` · ${dossier.bron_label}`}
            {dossier.onderwerpen.length > 0
              ? ` · ${dossier.onderwerpen.join(", ")}`
              : ""}
          </span>
          <span className="wp-meta" data-status-uitleg>
            {dossier.status_uitleg}
            {dossier.foutbericht ? ` ${dossier.foutbericht}` : ""}
          </span>
        </div>
      </div>
      <div className="wp-blok">
        <h4>
          <span>De zaak</span>
          <span className="wp-r">
            <span className="wp-actiemenu" ref={omhulsel}>
              <button
                type="button"
                ref={setKnop}
                className="wp-knop"
                data-acties
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((aan) => !aan)}
              >
                <IcoonBeheer maat={13} />
                Acties
                <IcoonPijlOnder maat={12} />
              </button>

              <Zwevendmenu
                anker={knop}
                open={open}
                sluit={() => setOpen(false)}
                testAttribuut="review-acties"
              >
                <>
                  <button
                    type="button"
                    role="menuitem"
                    data-actie="kortingscode"
                    disabled={kortingscode?.stand !== "werkend"}
                    title={kortingscode?.reden_uit ?? undefined}
                  >
                    <IcoonEuro maat={13} />
                    Kortingscode sturen
                    <small>
                      {kortingscode?.stand === "werkend"
                        ? "privé, niet in de publieke reactie"
                        : (kortingscode?.reden_uit ??
                          "Deze actie staat nog niet in de actie-laag.")}
                    </small>
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    data-actie="case_tap"
                    disabled={!caseKan}
                    title={caseRedenUit ?? undefined}
                    onClick={() => {
                      setOpen(false);
                      setCaseTekst(
                        `Klant meldt in een ${dossier.bron_label}-review: "${dossier.tekst}"`,
                      );
                      setCaseOpen(true);
                    }}
                  >
                    <IcoonSchild maat={13} />
                    Melding bij {zending?.vervoerder ?? "de vervoerder"}
                    <small>
                      {caseKan
                        ? `zending ${trackingcode}`
                        : (caseRedenUit ?? "")}
                    </small>
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    data-actie="prive_mail"
                    disabled={!zaakId}
                    title={
                      zaakId
                        ? undefined
                        : "Er is nog geen zaak bij deze review; zonder zaak kan er niets verstuurd worden."
                    }
                    onClick={() => {
                      setOpen(false);
                      opPriveMail();
                    }}
                  >
                    <IcoonMail maat={13} />
                    Klant privé mailen
                    <small>opent de gedeelde reactietegel</small>
                  </button>
                </>
              </Zwevendmenu>
            </span>
          </span>
        </h4>

        <dl className="wp-feiten">
          <div className="wp-z">
            <dt>Zending</dt>
            <dd>
              {zending ? (
                <>
                  {zending.vervoerder ?? "Vervoerder onbekend"}{" "}
                  {zending.code ?? ""}
                  {zending.track_url ? (
                    <a
                      className="wp-ext"
                      href={zending.track_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <IcoonExtern maat={12} />
                      Track en trace
                    </a>
                  ) : null}
                  {zending.status ? ` · ${zending.status}` : null}
                </>
              ) : (
                <span className="wp-leegregel">
                  Geen zending gevonden bij deze review.
                </span>
              )}
            </dd>
          </div>

          <div>
            <dt>Regels</dt>
            <dd>
              <span className="wp-leegregel">
                Geen kaderregel gekoppeld aan deze review.
              </span>
            </dd>
          </div>

          <div className="wp-z">
            <dt>Klant</dt>
            <dd>
              {dossier.klant_id ? (
                <a href={`/klanten/${dossier.klant_id}`}>
                  {dossier.klant_naam ?? "Klantpagina"}
                </a>
              ) : (
                <span className="wp-leegregel">
                  Deze review is niet aan een klant gekoppeld.
                </span>
              )}
              {dossier.ordernummer ? ` · order ${dossier.ordernummer}` : null}
            </dd>
          </div>
        </dl>

        {caseOpen ? (
          <div className="wp-bevestig" role="dialog" aria-label="Melding bij de vervoerder">
            <p>
              <b>
                Melding bij {zending?.vervoerder ?? "de vervoerder"} over
                zending {trackingcode}
              </b>
            </p>
            <textarea
              value={caseTekst}
              aria-label="Tekst voor de vervoerder"
              data-case-tekst
              style={{ width: "100%", minHeight: 80, font: "inherit" }}
              onChange={(e) => setCaseTekst(e.target.value)}
            />
            <div className="wp-bevestig-rij">
              <button
                type="button"
                className="wp-knop"
                onClick={() => setCaseOpen(false)}
              >
                Annuleren
              </button>
              <button
                type="button"
                className="wp-knop wp-primair"
                data-case-versturen
                disabled={meldAan.isPending || caseTekst.trim().length === 0}
                onClick={() => meldAan.mutate()}
              >
                {meldAan.isPending ? "Bezig…" : "Melding aanmaken"}
              </button>
            </div>
          </div>
        ) : null}

        {uitkomst ? (
          <div
            className="wp-uitkomst"
            data-uitkomst={uitkomst.status}
            role={uitkomst.status === "mislukt" ? "alert" : "status"}
          >
            {uitkomst.status === "mislukt"
              ? `Mislukt: ${uitkomst.foutmelding ?? "onbekende fout"}. Er staat niets open bij de vervoerder.`
              : `Melding staat klaar bij de vervoerder${
                  (uitkomst.resultaat?.ticket_nummer as string | undefined)
                    ? `, ticket ${uitkomst.resultaat?.ticket_nummer as string}`
                    : ""
                }. Er loopt een wekker van drie werkdagen op deze zaak.`}
          </div>
        ) : null}

        {fout ? (
          <div className="wp-uitkomst" role="alert">
            {fout}
          </div>
        ) : null}
      </div>

    </>
  );
}
